/**
 * The full Thunder Energy horizontal lockup — the bolt plus the THUNDER wordmark.
 *
 * Used only where there is real room for it. It does NOT go in the 64px app header or in
 * any small slot: the artwork is 820x221 (3.71:1), so at header scale the letters fall
 * below legibility and the whole lockup would have to shrink to a smudge. Those slots get
 * `BrandMark` — the square-ish monogram — instead.
 *
 * WHY TWO ARTWORK FILES
 *
 * The wordmark letters are a SOLID fill in the source art — #fff in the light-ink file,
 * #131325 in the dark-ink one — not `currentColor`, so the file itself has to change with
 * the surface it sits on. Light ink on a light header is invisible, and so is the reverse.
 *
 * `ink` names the colour of the LETTERS, not of the surface:
 *
 *   'light'  white letters — for a surface that is dark in EVERY theme
 *   'dark'   near-black letters — for a surface that is light in every theme
 *   'auto'   follows the console theme, in CSS only (`.lm-wordmark` in app.css)
 *
 * `auto` is the default because it is the safe one: a new call site that forgets to think
 * about theme gets the correct artwork rather than an invisible one. Both images are always
 * in the DOM and CSS hides one, which is what makes it flash-free — a JS theme read happens
 * after first paint, so a dark-theme user would see one frame of black-on-black. The CSS
 * covers all THREE theme states, including the system default, which sets no `data-theme`
 * attribute at all and is reachable only through `prefers-color-scheme`.
 *
 * A call site should still pin `ink` when its surface is theme-independent — the sign-in
 * frame hard-codes #0a0c12 and stays dark under `data-theme="light"`, so `auto` would put
 * black letters on near-black there. That is the one case `auto` cannot get right, because
 * the console theme and the surface disagree.
 */
import type { ReactNode } from 'react';
import darkInkUrl from '@/assets/brand/thunder-wordmark-dark.svg';
import lightInkUrl from '@/assets/brand/thunder-wordmark-light.svg';
import { PRODUCT } from '@/product/profile';
import { CratusLockup } from './CratusBrand';

/**
 * Aspect ratio of the light-ink artwork (viewBox 820.47 x 220.88 = 3.714:1).
 *
 * The dark-ink file is cropped slightly tighter (649.5 x 182.6 = 3.556:1), so the two are
 * NOT interchangeable at a fixed width. The box is sized from this one and app.css lets each
 * image `contain` inside it, which keeps both undistorted and the box a constant size in
 * either theme.
 */
const ASPECT = 820.47 / 220.88;

export function BrandWordmark({
  height = 28,
  ink = 'auto',
}: {
  /** Rendered height in px. Width follows from the artwork's aspect ratio. */
  height?: number;
  ink?: 'light' | 'dark' | 'auto';
}): ReactNode {
  /* A white-label build draws its own lockup. Cratus's is one colour on every surface, so
     `ink` has nothing to switch there — see `CratusBrand.tsx`. */
  /* `__PRODUCT__`, the build's literal: each build keeps only its own artwork. */
  if (__PRODUCT__ === 'cratus' && PRODUCT.id === 'cratus') return <CratusLockup height={height} />;
  /* Width is derived and set on the box, so the lockup reserves its space before the data
     URI decodes and cannot shift the layout around it. */
  const width = Math.round(height * ASPECT);
  return (
    /*
     * The accessible name lives on the WRAPPER, not on either <img>. Which image CSS shows
     * depends on the theme, so naming both would announce the lockup twice if a stylesheet
     * ever failed to load; naming the wrapper and marking both images decorative gives
     * exactly one name in every case.
     */
    <span className="lm-wordmark" data-ink={ink} style={{ width, height }} role="img" aria-label="Thunder Energy">
      <img className="lm-ink-light" src={lightInkUrl} alt="" />
      <img className="lm-ink-dark" src={darkInkUrl} alt="" />
    </span>
  );
}
